import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Globe,
  Building2,
  Type,
  FileText,
  Palette,
  Save,
  Eye,
  Plus,
  Sparkles
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const NewSite = () => {
  const navigate = useNavigate()
  const [selectedTemplate, setSelectedTemplate] = useState('blank')

  const { register, handleSubmit, watch, formState: { errors } } = useForm()
  const siteName = watch('name', '')

  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    'clients',
    () => apiService.get('/clients').then(res => res.data)
  )

  const clients = clientsData?.clients || []

  const createSiteMutation = useMutation(
    (siteData) => apiService.post('/sites', siteData),
    {
      onSuccess: (response) => {
        toast.success('Site created successfully!')
        navigate(`/admin/editor?site=${response.data.id}`)
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to create site'
        toast.error(errorMessage)
      }
    }
  )

  const onSubmit = (data) => {
    createSiteMutation.mutate({
      name: data.name,
      client_id: data.client_id,
      template_id: null,
      description: data.description || ''
    })
  }

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

  const templates = [
    { id: 'blank', name: 'Blank Site', description: 'Start with a clean slate', category: 'Basic' },
    { id: 'business', name: 'Business Website', description: 'Professional business template', category: 'Business' },
    { id: 'portfolio', name: 'Portfolio', description: 'Showcase your work', category: 'Creative' },
    { id: 'landing', name: 'Landing Page', description: 'Single page marketing site', category: 'Marketing' },
  ]

  if (clientsLoading) {
    return (
      <div style={{ paddingTop: 16 }}>
        <div className="flex items-center justify-center" style={{ height: 256 }}>
          <LoadingSpinner size="lg" text="Loading clients..." />
        </div>
      </div>
    )
  }

  return (
    <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 32 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-4">
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate('/admin/sites')}>
            <ArrowLeft style={{ width: 20, height: 20 }} />
          </button>
          <div>
            <h1 className="h2">Create New Site</h1>
            <p className="text-muted" style={{ fontSize: 13 }}>Set up a new website for your client</p>
          </div>
        </div>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32 }}>
        {/* Left column */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
        >
          {/* Site Information */}
          <div className="card card-pad">
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
              <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                <Globe style={{ width: 20, height: 20, color: 'var(--accent)' }} />
              </div>
              <div>
                <p className="font-semibold">Site Information</p>
                <p className="text-muted" style={{ fontSize: 12 }}>Configure the basic details for your new website</p>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                  Site Name <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Site name is required' })}
                  className="input"
                  placeholder="Enter site name"
                  style={errors.name ? { borderColor: 'var(--danger)' } : {}}
                />
                {errors.name && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.name.message}</p>}
                {siteName && (
                  <p className="text-muted" style={{ fontSize: 12, marginTop: 6, background: 'var(--surface-2)', padding: '6px 10px', borderRadius: 'var(--radius-sm)' }}>
                    <strong>URL slug:</strong> {generateSlug(siteName)}
                  </p>
                )}
              </div>
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                  Client <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <select
                  {...register('client_id', { required: 'Please select a client' })}
                  className="input"
                  style={errors.client_id ? { borderColor: 'var(--danger)' } : {}}
                >
                  <option value="">Select a client</option>
                  {clients?.map((client) => (
                    <option key={client.id} value={client.id}>{client.company_name}</option>
                  ))}
                </select>
                {errors.client_id && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.client_id.message}</p>}
              </div>
              <div>
                <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Description</label>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="input"
                  placeholder="Optional description for this site (helps organize multiple sites for the same client)"
                  style={{ resize: 'none' }}
                />
              </div>
            </form>
          </div>

          {/* Template Selection */}
          <div className="card card-pad">
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
              <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                <Palette style={{ width: 20, height: 20, color: 'var(--accent)' }} />
              </div>
              <div>
                <p className="font-semibold">Choose Template</p>
                <p className="text-muted" style={{ fontSize: 12 }}>Select a starting template for your website</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {templates.map((template) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setSelectedTemplate(template.id)}
                  style={{
                    position: 'relative', cursor: 'pointer', borderRadius: 'var(--radius-lg)',
                    border: selectedTemplate === template.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                    padding: 16, transition: 'all 0.2s',
                    background: selectedTemplate === template.id ? 'var(--accent-soft)' : 'var(--surface)'
                  }}
                >
                  <div style={{ aspectRatio: '16/9', background: 'var(--surface-2)', borderRadius: 'var(--radius)', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileText style={{ width: 28, height: 28, color: 'var(--text-faint)' }} />
                  </div>
                  <p className="font-semibold" style={{ marginBottom: 2, fontSize: 13 }}>{template.name}</p>
                  <p className="text-soft" style={{ fontSize: 12, marginBottom: 8 }}>{template.description}</p>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 11, background: 'var(--surface-2)', color: 'var(--text-soft)' }}>
                    {template.category}
                  </span>
                  {selectedTemplate === template.id && (
                    <div style={{ position: 'absolute', top: 10, right: 10, width: 22, height: 22, background: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#fff', fontSize: 12 }}>✓</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Preview & Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ position: 'sticky', top: 24, alignSelf: 'start' }}
        >
          <div className="card card-pad">
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
              <div style={{ padding: 8, background: 'var(--success-soft)', borderRadius: 'var(--radius)' }}>
                <Eye style={{ width: 20, height: 20, color: 'var(--success)' }} />
              </div>
              <div>
                <p className="font-semibold">Preview &amp; Create</p>
                <p className="text-muted" style={{ fontSize: 12 }}>Review and create your website</p>
              </div>
            </div>

            {/* Template Preview */}
            <div style={{ marginBottom: 16 }}>
              <p className="font-semibold text-soft" style={{ fontSize: 12, marginBottom: 10 }}>Selected Template</p>
              <div style={{ aspectRatio: '16/9', background: 'var(--surface-2)', borderRadius: 'var(--radius)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <FileText style={{ width: 40, height: 40, color: 'var(--text-faint)', margin: '0 auto 6px' }} />
                  <p className="text-muted" style={{ fontSize: 12 }}>{templates.find(t => t.id === selectedTemplate)?.name}</p>
                </div>
              </div>
            </div>

            {/* Site Summary */}
            {siteName && (
              <div style={{ marginBottom: 16, padding: 14, background: 'var(--accent-soft)', borderRadius: 'var(--radius)', border: '1px solid var(--border-accent)' }}>
                <p className="font-semibold" style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 8 }}>Site Summary</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                  <div className="flex justify-between">
                    <span className="text-muted">Name:</span>
                    <span className="font-medium">{siteName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Slug:</span>
                    <span className="font-medium" style={{ fontFamily: 'var(--font-mono)' }}>/{generateSlug(siteName)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Template:</span>
                    <span className="font-medium">{templates.find(t => t.id === selectedTemplate)?.name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-primary w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createSiteMutation.isLoading}
                style={{ height: 44 }}
              >
                {createSiteMutation.isLoading ? (
                  <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', marginRight: 8 }} />Creating Site...</>
                ) : (
                  <><Save style={{ width: 16, height: 16, marginRight: 8 }} />Create Site</>
                )}
              </button>
              <button
                className="btn w-full"
                onClick={() => navigate('/admin/sites')}
                style={{ height: 44 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default NewSite
